import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import WorkCard from "../components/WorkCard";
import BottomNavbar from "../components/BottomNavbar";
import Header from "../components/Header";
import "./css/Dashboard.css";

import profileIncompleteImage from "../assets/images/profile_incomplete.png";
import noDataImage from "../assets/images/no_data.png";

export default function Dashboard() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingWorks, setLoadingWorks] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [lang, setLang] = useState(localStorage.getItem("app_language") || "en");
  const [contractorAds, setContractorAds] = useState([]);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const adIntervalRef = useRef(null);

  const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
  const VERSION = process.env.REACT_APP_VERSION;
  const LIMIT = 20;

  // Translations
  const t = {
    en: {
      loading: "Loading",
      hi: "Hi",
      filter: "Filter",
      profileCompletion: "Profile Completion",
      basicDetails: "Basic Details",
      pastWorks: "Past Works",
      workPreferences: "Work Preferences",
      completed: "Completed",
      pending: "Pending",
      completeProfile: "Complete profile",
      editProfile: "Edit profile",
      findingWorks: "Start posting your work requirements.",
      addWork: "Add Work",
      completeProfileToStart: "Complete your profile to start posting new works.",
      lookingForPastWorks: "Looking for your past works ?",
      clickHere: "Click Here",
      noWorkAvailable: "No work available",
      availableWorks: "Your Current Active Works",
      sponsoredAd: "Sponsored Ad",
    },
    ar: {
      loading: "جاري التحميل",
      hi: "مرحباً",
      filter: "تصفية",
      profileCompletion: "اكتمال الملف الشخصي",
      basicDetails: "التفاصيل الأساسية",
      pastWorks: "الأعمال السابقة",
      workPreferences: "تفضيلات العمل",
      completed: "مكتمل",
      pending: "معلق",
      completeProfile: "إكمال الملف الشخصي",
      editProfile: "تعديل الملف الشخصي",
      findingWorks: "ابدأ بنشر متطلبات العمل الخاصة بك.",
      completeProfileToStart: "أكمل ملفك الشخصي لبدء نشر الأعمال الجديدة.",
      noWorkAvailable: "لا توجد أعمال متاحة",
      availableWorks: "أعمالك النشطة الحالية",
      addWork: "أضف عمل",
      lookingForPastWorks: "تبحث عن أعمالك السابقة؟",
      clickHere: "انقر هنا",
      sponsoredAd: "إعلان مدعوم",
    },
  }[lang];

  // Fetch profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/profile/`,
          "GET",
          null,
          {}
        );

        if (data && data.response_code === 200 && data.response) {
          const profileData = data.response;

          // Set language preference
          if (profileData.language_preference) {
            localStorage.setItem("app_language", profileData.language_preference);
            setLang(profileData.language_preference);
          }

          setProfile(profileData);

          // Check if profile_completed_step is not 5, navigate to completion step
          const preProfileCompleted = profileData.pre_profile_completed;
          if (!preProfileCompleted) {
            navigate(`/profile-step-1`);
            return;
          }

          // If profile is complete and all sections are done, fetch works and ads
          if (
            profileData.profile_completed
          ) {
            // Fetch works after a small delay to ensure state is updated
            setTimeout(() => {
              fetchWorks(1, null);
              fetchAppDetails();
            }, 100);
          }
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        if (err.code !== 404) {
          showToast(err.message || "Failed to load profile", "error");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [USER_BASE_URL, VERSION]);

  // Fetch app details (for ads)
  const fetchAppDetails = useCallback(async () => {
    try {
      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/profile/app-details`,
        "GET",
        null,
        {}
      );

      if (data && data.response_code === 200 && data.response) {
        const ads = data.response.user_ads || [];
        setContractorAds(ads);
        if (ads.length > 0) {
          setCurrentAdIndex(0);
        }
      }
    } catch (err) {
      console.error("Error fetching app details:", err);
      // Don't show toast for ads, just fail silently
    }
  }, [USER_BASE_URL, VERSION]);

  // Fetch works
  const fetchWorks = useCallback(
    async (page = 1) => {
      try {
        setLoadingWorks(true);
        const url = `${USER_BASE_URL}/${VERSION}/work/?limit=${LIMIT}&page=${page}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "app-version": "1.0.0",
          },
        });

        const data = await response.json();

        if (data && data.response_code === 200) {
          if (page === 1) {
            setWorks(data.response || []);
          } else {
            setWorks((prev) => [...prev, ...(data.response || [])]);
          }
          setHasMorePages((data.response || []).length === LIMIT);
          setCurrentPage(page);
        }
      } catch (err) {
        console.error("Error fetching works:", err);
        showToast(err.message || "Failed to load works", "error");
      } finally {
        setLoadingWorks(false);
      }
    },
    [USER_BASE_URL, VERSION]
  );

  // Infinite scroll observer ref
  const observerTarget = useRef(null);

  // Handle infinite scroll
  const handleLoadMore = useCallback(() => {
    if (!loadingWorks && hasMorePages) {
      fetchWorks(currentPage + 1);
    }
  }, [fetchWorks, loadingWorks, hasMorePages, currentPage]);

  // Infinite scroll effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMorePages && !loadingWorks) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [handleLoadMore, hasMorePages, loadingWorks]);

  // Auto-rotate ads slideshow
  useEffect(() => {
    if (contractorAds.length > 1) {
      adIntervalRef.current = setInterval(() => {
        setCurrentAdIndex((prev) => (prev + 1) % contractorAds.length);
      }, 5000); // Change ad every 5 seconds

      return () => {
        if (adIntervalRef.current) {
          clearInterval(adIntervalRef.current);
        }
      };
    }
  }, [contractorAds.length]);

  // Handle ad click
  const handleAdClick = (redirectUrl) => {
    if (redirectUrl && redirectUrl !== "#") {
      window.open(redirectUrl, "_blank", "noopener,noreferrer");
    }
  };

  // Calculate profile completion percentage
  const calculateCompletionPercentage = () => {
    if (!profile) return 0;
    if (profile.profile_completed) return 100;
    return 25;
  };

  // Get completion status color
  const getCompletionColor = () => {
    const percentage = calculateCompletionPercentage();
    if (percentage === 100) return "#10b981"; // Green
    if (percentage >= 50) return "#f59e0b"; // Orange
    return "#EC1C24"; // Red
  };

  // Loading screen
  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  const completionPercentage = calculateCompletionPercentage();
  const isProfileComplete = profile.profile_completed;

  return (
    <div className="dashboard-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Header */}
      <Header lang={lang} />
      <div className="dashboard-greeting">
        <h2 className="greeting-text">
          {t.hi}, {profile.name || (lang === "ar" ? "مستخدم" : "User")} 👋
        </h2>
      </div>

      {/* Profile Completion Card */}
      {!isProfileComplete && (
        <div className="profile-completion-card">
          <div className="completion-header">
            <h3 className="completion-title">{t.profileCompletion}</h3>
            {completionPercentage === 100 && (
              <span className="completion-percentage">{completionPercentage}%</span>
            )}
          </div>
          <div className="completion-progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${completionPercentage}%`,
                backgroundColor: getCompletionColor(),
              }}
            ></div>
          </div>
          <div className="completion-items">
            <div className="completion-item">
              {profile.profile_completed ? (
                <i className="bx bx-check-circle completed-icon"></i>
              ) : (
                <i className="bx bx-radio-circle pending-icon"></i>
              )}
              <span>
                {profile.profile_completed
                  ? `${t.basicDetails} ${t.completed}`
                  : `${t.basicDetails} ${t.pending}`}
              </span>
            </div>
          </div>
          <div className="completion-footer">
            {completionPercentage === 100 ? (
              <button
                onClick={() => navigate("/profile")}
                className="complete-profile-link"
              >
                {t.editProfile} &gt;
              </button>
            ) : (
              <button
                onClick={() => navigate("/profile")}
                className="complete-profile-link"
              >
                {t.completeProfile} &gt;
              </button>
            )}
          </div>
        </div>
      )}

      {/* Sponsored Ads Section - Only show if profile is completed */}
      {isProfileComplete && contractorAds.length > 0 && (
        <div className="sponsored-ads-section">
          <h3 className="sponsored-ads-title">{t.sponsoredAd}</h3>
          <div className="sponsored-ads-container">
            <div className="sponsored-ads-slideshow">
              {contractorAds.map((ad, index) => (
                <img
                  key={ad._id || index}
                  src={ad.url}
                  alt="Sponsored Ad"
                  className={`sponsored-ad-image ${
                    index === currentAdIndex ? "active" : ""
                  }`}
                  onClick={() => handleAdClick(ad.redirect_url)}
                  style={{ cursor: ad.redirect_url && ad.redirect_url !== "#" ? "pointer" : "default" }}
                />
              ))}
              {contractorAds.length > 1 && (
                <div className="ad-indicators">
                  {contractorAds.map((_, index) => (
                    <span
                      key={index}
                      className={`ad-indicator ${
                        index === currentAdIndex ? "active" : ""
                      }`}
                      onClick={() => {
                        if (adIntervalRef.current) {
                          clearInterval(adIntervalRef.current);
                          adIntervalRef.current = null;
                        }
                        setCurrentAdIndex(index);
                        // Restart auto-rotation after 5 seconds
                        setTimeout(() => {
                          if (contractorAds.length > 1) {
                            adIntervalRef.current = setInterval(() => {
                              setCurrentAdIndex((prev) => (prev + 1) % contractorAds.length);
                            }, 5000);
                          }
                        }, 5000);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="dashboard-content">
        {!isProfileComplete ? (
          // Profile not complete - show illustration
          <div className="no-content-container">
            <img
              src={profileIncompleteImage}
              alt="Profile Incomplete"
              className="no-content-image"
            />
            <p className="no-content-message">{t.completeProfileToStart}</p>
          </div>
        ) : loadingWorks && works.length === 0 ? (
          // Loading works
          <div className="dashboard-loading-top">
            <div className="spinner"></div>
            <p>{t.loading}</p>
          </div>
        ) : works.length === 0 ? (
          // No works available
          <div className="no-content-container">
            <img
              src={noDataImage}
              alt="No Work Posted"
              className="no-content-image"
            />
            <p className="no-content-message">{t.findingWorks}</p>
            <button
              className="add-work-btn"
              onClick={() => navigate("/add-work")}
            >
              {t.addWork}
            </button>
            <p className="past-works-link-text">
              {t.lookingForPastWorks}{" "}
              <span
                className="past-works-link"
                onClick={() => navigate("/past-works")}
              >
                {t.clickHere}
              </span>
            </p>
          </div>
        ) : (
          // Works list
          <div className="works-section">
            <h2 className="works-section-title">{t.availableWorks}</h2>
            <div className="works-list">
              {works.map((work) => (
                <WorkCard key={work._id} work={work} lang={lang} />
              ))}
              {/* Infinite scroll trigger */}
              {hasMorePages && (
                <div ref={observerTarget} className="infinite-scroll-trigger">
                  {loadingWorks && (
                    <div className="loading-more-works">
                      <div className="spinner"></div>
                      <span>{t.loading}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <BottomNavbar currentPage="home" lang={lang} />
    </div>
  );
}
